// Sync data-theme as early as the module graph allows (head theme-bootstrap.js still wins for FOUC).
import '@cast/shared-theme-data-access';

import '@cast/split-me/monitoring/sentry';

import { CssBaseline } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ModalProvider } from 'mui-modal-provider';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Routes,
} from 'react-router-dom';

import { attachAnalyticsCLI } from '@cast/analytics';
import {
  ChartConfigProvider,
  ThemePaletteCssVars,
  SnackbarsProvider,
} from '@cast/design-system';
import {
  AccessControlProvider,
  AbilityProvider,
} from '@cast/infrastructure-abilities';
import { createJotaiRouter } from '@cast/infrastructure-navigation';
import { OrganizationsProvider } from '@cast/infrastructure-organization';
import { RootErrorBoundary } from '@cast/shared-layout-ui';
import { addChatbotStateOverrides } from '@cast/features-organization-shared-opspilot';
import {
  ToolboxPanel,
  addAbilityOverrides,
  AgentationWrapper,
  HubspotContactProvider,
  Initializer,
  queryClient,
  ThemeProvider,
  TrialProvider,
  TrialNudgeUserManager,
  InitApp,
  AuthController,
  App,
  InvitationPage,
} from '@cast/split-me';
import { isE2E, toggleAllLoggers, toggleLogger } from '@cast/utils';

import '@cast/split-me/assets/fonts/fonts.css';
import '@cast/design-system/tokens.css';
import '@cast/design-system/typography.css';
import './app-tokens.css';

const chartConfig = isE2E() ? { isAnimationActive: false } : {};

export const router = createJotaiRouter(
  createBrowserRouter(
    createRoutesFromElements(
      <Route
        path="*"
        element={
          <Routes>
            <Route path="/auth/*" Component={AuthController} />
            <Route
              path="/organization/join/:invitationId"
              Component={() => (
                <QueryClientProvider client={queryClient}>
                  <SnackbarsProvider>
                    <InitApp initAuth initOrganization>
                      <InvitationPage />
                    </InitApp>
                  </SnackbarsProvider>
                </QueryClientProvider>
              )}
            />
            <Route
              path="*"
              Component={() => (
                <DndProvider backend={HTML5Backend}>
                  <QueryClientProvider client={queryClient}>
                    <RootErrorBoundary>
                      <SnackbarsProvider>
                        <InitApp initAuth initOrganization>
                          <OrganizationsProvider>
                            <AccessControlProvider useCurrentUser>
                              <AbilityProvider>
                                <TrialProvider>
                                  <Initializer>
                                    <HubspotContactProvider>
                                      <ModalProvider>
                                        <ChartConfigProvider {...chartConfig}>
                                          <ThemePaletteCssVars />
                                          <App />
                                          <ToolboxPanel />
                                          <AgentationWrapper />
                                          <TrialNudgeUserManager />
                                        </ChartConfigProvider>
                                      </ModalProvider>
                                    </HubspotContactProvider>
                                  </Initializer>
                                </TrialProvider>
                              </AbilityProvider>
                            </AccessControlProvider>
                          </OrganizationsProvider>
                        </InitApp>
                        {!isE2E() && (
                          <ReactQueryDevtools
                            initialIsOpen={false}
                            buttonPosition="bottom-right"
                          />
                        )}
                      </SnackbarsProvider>
                    </RootErrorBoundary>
                  </QueryClientProvider>
                </DndProvider>
              )}
            />
          </Routes>
        }
      />
    )
  )
);

const runApp = () => {
  createRoot(document.getElementById('root')!).render(
    <StyledEngineProvider injectFirst>
      <ThemeProvider>
        <CssBaseline />
        <RouterProvider router={router} />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

if (import.meta.env.VITE_ENABLE_MSW) {
  import('@cast/unitTesting').then(({ getWorker }) => {
    getWorker()
      .start()
      .then(() => runApp());
  });
} else {
  runApp();
}

// Functions, callable in dev console
attachAnalyticsCLI();
// TODO (Justas - NX migration): Make `window.d.ts` available across libs
(window as any).toggleLogger = toggleLogger;
(window as any).toggleAllLoggers = toggleAllLoggers;

if (isE2E()) {
  addAbilityOverrides();
  addChatbotStateOverrides();
}
